import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";

import { Directory, FileReadWithUrl, ItemsInner } from "@nens/rana/api-v1";
import { getRanaApiV1 } from "@nens/rana/api-v1";

import { ProjectContext } from "@/stores/project-provider";
import { descriptorIsLoading } from "@/utils/descriptor-is-loading";
import { getAllParentPaths, getParentPath } from "@/utils/get-file-parent-path";
import { removeEmptyPathSegments } from "@/utils/remove-empty-path-segments";

import { createUpdateForQueries } from "./hooks/create-updater-for-queries";
import { invalidateIfOutdated } from "./hooks/if-outdated";
import { queryDirectly } from "./hooks/query-directly";
import { useDebounceInvalidation } from "./hooks/use-debounce-invalidation";

// UTIL

const getRefetchInterval = (file: FileReadWithUrl | ItemsInner | undefined) => {
  if (!file || file.type === "directory") return false;
  const { dataType } = file;
  if (!dataType) return 2500;
  // Shapefile is not a supported preview format. The backend automatically converts it to a geopackage.
  // So we should refetch until its deleted and the user will be redirected to the .gpkg version (use-project-files-data.ts)
  if (dataType === "shapefile") return 2500;
  if (descriptorIsLoading(file.descriptor)) return 10_000;
  return false;
};

// Single

const getFileQuery = (projectId: string, path: string | undefined, ref?: string) => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["project", projectId, "file-state", ref, path],
    queryFn: async () => {
      if (!path) throw Error("Never happens");
      return await getRanaApiV1().FilesApi.projectFileStat({ projectId, path, ref });
    },
    refetchInterval: (query) => getRefetchInterval(query.state.data),
    refetchOnMount: (query) => (getRefetchInterval(query.state.data) ? "always" : true),
    enabled: !!path,
  });
};

export const useFileQuery = (id: string | undefined, ref?: string) => {
  const { id: projectId } = useContext(ProjectContext);

  return useQuery(getFileQuery(projectId, id, ref));
};

export const useFileQueryDirectly = () => {
  const { id: projectId } = useContext(ProjectContext);
  const queryClient = useQueryClient();
  return async (id: string, ref?: string) =>
    queryDirectly(queryClient, getFileQuery(projectId, id, ref));
};

export const useFileQueries = (fileIdsAndRefs: { id: string; ref?: string }[]) => {
  const { id: projectId } = useContext(ProjectContext);

  return useQueries({
    queries: fileIdsAndRefs.map(({ id, ref }) => getFileQuery(projectId, id, ref)),
  });
};

// LIST

const getFileDirectoryQuery = (projectId: string, path: string, ref?: string) => {
  // Split the path into segments to help with invalidation
  // For example, if we have a path like "folder1/folder2/folder3/file.txt", the query key will be
  // ["project", projectId, "file-directory", ref, "folder1", "folder2", "file.txt"]
  // instead of
  // ["project", projectId, "file-directory", ref, "folder1/folder2/file.txt"].
  // This way we can invalidate all children of "folder1" by invalidating all queries that start with
  // ["project", projectId, "file-directory", ref, "folder1"].
  const pathSegments = removeEmptyPathSegments(path.split("/"));
  return getRanaApiV1().getQueryOptions({
    queryKey: ["project", projectId, "file-directory", ref, ...pathSegments],
    queryFn: async () => await getRanaApiV1().FilesApi.projectFileLs({ projectId, path, ref }),
    refetchInterval: (query) => query.state.data?.items.map(getRefetchInterval).find(Boolean),
    refetchOnMount: (query) =>
      query.state.data?.items.map(getRefetchInterval).find(Boolean) ? "always" : true,
  });
};

export const useFileDirectoryQueryDirectlyWithProjectId = () => {
  const queryClient = useQueryClient();
  return async (projectId: string, path: string) =>
    queryDirectly(queryClient, getFileDirectoryQuery(projectId, path));
};

export const useFileDirectoryQueryDirectly = () => {
  const { id: projectId } = useContext(ProjectContext);
  const queryClient = useQueryClient();
  return async (path: string) => queryDirectly(queryClient, getFileDirectoryQuery(projectId, path));
};

export const useFileDirectoryQuery = (path: string, ref?: string) => {
  const { id: projectId } = useContext(ProjectContext);
  return useQuery(getFileDirectoryQuery(projectId, path, ref));
};

export const useFileDirectoryQueries = (directoryIdsAndRefs: { id: string; ref?: string }[]) => {
  const { id: projectId } = useContext(ProjectContext);
  return useQueries({
    queries: directoryIdsAndRefs.map(({ id, ref }) => {
      return getFileDirectoryQuery(projectId, id, ref);
    }),
  });
};

// INVALIDATION

// If the path is a directory, we need to invalidate the directory query instead of the file query
const getSingleQueryKey = (projectId: string, path: string) =>
  path.endsWith("/")
    ? getFileDirectoryQuery(projectId, path).queryKey
    : getFileQuery(projectId, path).queryKey;

// IMPORTANT: never call this directly. Always use `useInvalidateFilesAndHistoryQueriesIfOutdated`
export const useInvalidateFileQueriesIfOutdated = () => {
  const queryClient = useQueryClient();
  const { id: projectId } = useContext(ProjectContext);

  return async (finishedAt: Date, path: string) => {
    invalidateIfOutdated(queryClient, finishedAt, getSingleQueryKey(projectId, path));
    getAllParentPaths(path).forEach((parentPath) => {
      invalidateIfOutdated(
        queryClient,
        finishedAt,
        getFileDirectoryQuery(projectId, parentPath).queryKey,
        { exact: true }
      );
    });
  };
};

// IMPORTANT: never call this directly. Always use `useInvalidateFilesAndHistoryQueries`
export const useInvalidateFileQueries = () => {
  const queryClient = useQueryClient();
  // Cant get project id from context because otherwise it wont refresh correctly in file uploading provider
  return useDebounceInvalidation((projectId: string, path: string) => {
    queryClient.invalidateQueries({
      queryKey: getSingleQueryKey(projectId, path),
      refetchType: "all",
    });
    getAllParentPaths(path).forEach((parentPath) => {
      queryClient.invalidateQueries({
        queryKey: getFileDirectoryQuery(projectId, parentPath).queryKey,
        refetchType: "all",
        exact: true,
      });
    });
  });
};

// IMPORTANT: never call this directly. Always use `useInvalidateFilesAndHistoryQueries`
export const useUpdateFileQueries = () => {
  const { id: projectId } = useContext(ProjectContext);
  const queryClient = useQueryClient();

  const updater = createUpdateForQueries(queryClient, {
    getSingleQueryKey: (id) => getSingleQueryKey(projectId, id),
    getListQueryKey: (id) => getFileDirectoryQuery(projectId, getParentPath(id)).queryKey,
  });

  const deleteChildDirectoryQueries = (id: string) => {
    if (!id.endsWith("/")) return;
    const folderQueryKey = getFileDirectoryQuery(projectId, id).queryKey;
    queryClient.removeQueries({
      predicate: (query) => {
        const qKey = query.queryKey;
        if (!Array.isArray(qKey)) return false;
        if (!folderQueryKey.every((segment, idx) => segment === qKey[idx])) return false; // Check if the query key starts with the folder query key
        if (query.queryKey.length === folderQueryKey.length) return false; // Exact match for the directory
        return true; // All deeper paths are invalidated
      },
      refetchType: "all",
    });
  };

  const updateFileQuery = (beforeId: string, item: FileReadWithUrl | Directory | null) => {
    deleteChildDirectoryQueries(beforeId);
    if (item && item?.id !== beforeId) {
      deleteChildDirectoryQueries(item.id);
    }
    return updater(beforeId, item);
  };

  return updateFileQuery;
};
